/**
 * Contexto del post para conversaciones de tipo comentario (R3.1 + R3.2).
 *
 * - Bar fija arriba del thread: cuenta que publicó, contador de comentarios y
 *   de sin-responder, botón "Ver post" (permalink cuando esté disponible) y
 *   "Ver los N comentarios".
 * - Drawer lateral: lista TODOS los comentarios del post (cross-conversación)
 *   con estado respondido/pendiente y reply inline para no volver al inbox.
 *
 * thumbnail/caption/permalink se enriquecen via Meta cuando estén disponibles;
 * hoy el backend los devuelve null y la UI degrada con gracia.
 */
import { useState } from "react";
import { ExternalLink, ImageIcon, MessageSquare, Send, X } from "lucide-react";
import {
  usePostComments,
  useCommentPost,
  type PostContext,
} from "@/hooks/use-post-comments";
import { useSendMessage } from "@/hooks/use-send-message";
import { useQueryClient } from "@tanstack/react-query";
import { formatArrivalAge } from "@/lib/format";
import { isReclamo } from "@/lib/intent";
import { cn } from "@/lib/cn";
import type { ApiIncomingMessage } from "@/lib/adapters";

function initialFor(name: string | null | undefined): string {
  if (!name) return "?";
  return name.trim()[0]?.toUpperCase() ?? "?";
}

export function PostContextBar({
  clientSlug,
  commentId,
}: {
  clientSlug: string;
  commentId: string;
}) {
  // Resuelve el post padre del comentario via Meta (caption, imagen, permalink).
  const { data: postData, isLoading } = useCommentPost(clientSlug, commentId);
  const post = postData?.post;
  const mediaId = post?.mediaId ?? null;

  // Todos los comentarios del post (solo si ya tenemos el post id resuelto).
  const { data: thread } = usePostComments(clientSlug, mediaId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const count = thread?.post.totalComments ?? 0;
  const unanswered = thread?.post.unansweredCount ?? 0;

  return (
    <>
      <div className="px-6 py-2.5 border-b border-subtle bg-surface-1 flex items-center gap-3 flex-wrap">
        {/* Miniatura real del post (via Meta), placeholder si no cargó. */}
        <div className="flex-shrink-0 w-10 h-10 rounded-md bg-surface-2 border border-default flex items-center justify-center text-text-muted overflow-hidden">
          {post?.imageUrl ? (
            <img
              src={post.imageUrl}
              alt="Post"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <ImageIcon size={16} strokeWidth={1.5} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="font-sans text-xs text-text-muted">
            Comentario en post
            {post?.accountHandle && (
              <>
                {" · "}
                <span className="text-text-secondary">@{post.accountHandle}</span>
              </>
            )}
            {count > 0 && (
              <>
                {" · "}
                {count} comentario{count !== 1 ? "s" : ""}
                {unanswered > 0 && (
                  <span className="text-warning-text font-medium">
                    {" · "}
                    {unanswered} sin responder
                  </span>
                )}
              </>
            )}
          </div>
          <div className="font-sans text-sm text-text-primary line-clamp-2 leading-snug">
            {isLoading ? (
              <span className="text-text-muted">Cargando post…</span>
            ) : post?.caption ? (
              post.caption
            ) : (
              <span className="text-text-muted">Comentario en una publicación</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {post?.permalink && (
            <a
              href={post.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-default text-text-secondary hover:text-text-primary hover:bg-surface-2 font-sans text-xs font-medium transition-colors"
            >
              <ExternalLink size={13} strokeWidth={1.75} />
              Ver post
            </a>
          )}
          {count > 0 && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-default text-text-primary hover:bg-surface-2 font-sans text-xs font-medium transition-colors"
            >
              <MessageSquare size={13} strokeWidth={1.75} />
              Ver los {count} comentario{count !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>

      {drawerOpen && thread && (
        <PostCommentsDrawer
          post={thread.post}
          comments={thread.comments}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}

function PostCommentsDrawer({
  post,
  comments,
  onClose,
}: {
  post: PostContext;
  comments: ApiIncomingMessage[];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="relative h-full w-full max-w-[440px] bg-surface-1 border-l border-default shadow-xl flex flex-col">
        <header className="px-5 py-4 border-b border-subtle flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-sans text-base font-semibold text-text-primary">
              Comentarios del post
            </h3>
            <p className="font-sans text-xs text-text-muted mt-0.5">
              {post.totalComments} en total
              {post.unansweredCount > 0 && (
                <>
                  {" · "}
                  <span className="text-warning-text">
                    {post.unansweredCount} sin responder
                  </span>
                </>
              )}
              {post.accountHandle && ` · @${post.accountHandle}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {comments.length === 0 ? (
            <div className="px-5 py-8 text-sm text-text-muted text-center">
              Sin comentarios.
            </div>
          ) : (
            <ul className="flex flex-col">
              {comments.map((cm) => (
                <CommentRow key={cm.id} comment={cm} />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function CommentRow({ comment }: { comment: ApiIncomingMessage }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [text, setText] = useState("");
  const send = useSendMessage();
  const qc = useQueryClient();

  const answered = comment.state === "responded";
  const escalated = comment.state === "escalated";
  const name = comment.externalUsername ?? comment.externalUserId ?? "anon";

  function handleSend() {
    if (!comment.conversationId || !text.trim() || send.isPending) return;
    send.mutate(
      {
        conversationId: comment.conversationId,
        messageId: comment.id,
        body: text.trim(),
      },
      {
        onSuccess: () => {
          setText("");
          setReplyOpen(false);
          qc.invalidateQueries({ queryKey: ["post-comments"] });
        },
      },
    );
  }

  return (
    <li className="px-5 py-3.5 border-b border-subtle">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-surface-3 border border-default flex items-center justify-center font-sans text-xs font-semibold text-text-secondary">
          {initialFor(name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-sans text-sm font-medium text-text-primary truncate">
              @{name}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-mono text-[11px] tabular-nums text-text-muted">
                {formatArrivalAge(comment.receivedAt)}
              </span>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-sans font-medium border",
                  answered
                    ? "bg-success-bg text-success-text border-success-border"
                    : escalated
                      ? "bg-state-escalated-bg text-state-escalated-text border-state-escalated-border"
                      : "bg-warning-bg text-warning-text border-warning-border",
                )}
              >
                {answered ? "respondido" : escalated ? "escalado" : "pendiente"}
              </span>
            </div>
          </div>
          <p className="mt-1 font-sans text-sm text-text-secondary leading-snug whitespace-pre-wrap break-words">
            {comment.body}
          </p>

          {isReclamo(comment.body) && (
            <span
              title="Posible reclamo de producto (autoetiquetado)"
              className="mt-1.5 inline-flex items-center rounded-full border border-error-border bg-error-bg px-2 py-0.5 text-2xs font-sans font-medium text-error-text"
            >
              reclamo
            </span>
          )}

          {answered && comment.responseBody && (
            <div className="mt-2 rounded-lg bg-surface-2 border border-default px-3 py-2">
              <div className="font-sans text-2xs uppercase tracking-wider text-text-muted mb-1">
                Respuesta · {comment.respondedBy ?? "cm-ai"}
              </div>
              <p className="font-sans text-sm text-text-primary whitespace-pre-wrap break-words">
                {comment.responseBody}
              </p>
            </div>
          )}

          {!answered &&
            (replyOpen ? (
              <div className="mt-2">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  rows={2}
                  autoFocus
                  placeholder="Responder este comentario…"
                  className="w-full resize-none rounded-lg bg-surface-2 border border-default px-3 py-2 font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-magenta"
                />
                <div className="flex items-center justify-end gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setReplyOpen(false);
                      setText("");
                    }}
                    className="inline-flex items-center h-8 px-3 rounded-lg border border-default text-text-secondary hover:bg-surface-2 font-sans text-xs font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={!text.trim() || send.isPending}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-magenta text-white font-sans text-xs font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={12} strokeWidth={2} />
                    {send.isPending ? "Enviando…" : "Enviar"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setReplyOpen(true)}
                className="mt-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-default text-text-secondary hover:text-text-primary hover:bg-surface-2 font-sans text-xs font-medium transition-colors"
              >
                <Send size={12} strokeWidth={1.75} />
                Responder
              </button>
            ))}
        </div>
      </div>
    </li>
  );
}
